import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { PreviewIcon, EditIcon, CheckIcon, XIcon } from '../Icons/Icons';
import { useEvaluationStore } from '../../store/evaluationStore';
import { useUserSettingsStore } from '../../store/userSettingsStore';
import { encryptProductTitle, decryptProductTitle } from '../../utils/encryption';
import jsPDF from 'jspdf';
import './ProductDetailModal.css';

interface ProductDetailModalProps {
  isOpen: boolean;
  productIndex: number;
  onClose: () => void;
  onPreview?: (url: string, title: string, productIndex?: number) => void;
}

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  isOpen,
  productIndex,
  onClose,
  onPreview
}) => {
  const { t } = useTranslation();
  const { products, updateProduct } = useEvaluationStore();
  const { userSettings } = useUserSettingsStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editComment, setEditDetail] = useState('');
  const [editLink, setEditLink] = useState('');
  const [decryptedTitle, setDecryptedTitle] = useState('');

  const product = products[productIndex];
  const securityModeEnabled = userSettings?.securityMode || false;

  // 商品タイトルの復号化
  useEffect(() => {
    if (!product) return;

    const decryptTitle = async () => {
      if (securityModeEnabled && userSettings?.privateKey && product.reviewTitle.startsWith('encrypted:')) {
        try {
          const decrypted = await decryptProductTitle(product.reviewTitle, userSettings.privateKey);
          setDecryptedTitle(decrypted);
        } catch (error) {
          console.error(t('error.decryptionError'), error);
          setDecryptedTitle('[復号化エラー]');
        }
      } else {
        setDecryptedTitle(product.reviewTitle);
      }
    };

    decryptTitle();
  }, [product, securityModeEnabled, userSettings?.privateKey]);

  // 編集モード開始
  const startEdit = () => {
    setIsEditing(true);
    setEditTitle(decryptedTitle);
    setEditDetail(product.reviewComment);
    setEditLink(product.reviewLink || '');
  };

  // 編集を保存
  const saveEdit = async () => {
    if (!editTitle.trim() || !editComment.trim()) return;

    let titleToStore = editTitle.trim();

    // セキュリティモードが有効な場合は暗号化
    if (securityModeEnabled && userSettings?.publicKey) {
      try {
        titleToStore = await encryptProductTitle(editTitle.trim(), userSettings.publicKey);
      } catch (error) {
        console.error(t('error.encryptionError'), error);
        alert(t('productDetail.error.encryptionFailed'));
        return;
      }
    }

    updateProduct(productIndex, {
      reviewTitle: titleToStore,
      reviewComment: editComment.trim(),
      reviewLink: editLink.trim() || undefined
    });

    setIsEditing(false);
  };

  // 編集をキャンセル
  const cancelEdit = () => {
    setIsEditing(false);
    setEditTitle('');
    setEditDetail('');
    setEditLink('');
  };

  // プレビューを開く
  const handlePreview = () => {
    if (product.reviewLink && onPreview) {
      onPreview(product.reviewLink, decryptedTitle, productIndex);
    }
  };

  // 成績表PDF出力機能
  const generateGradeReport = () => {
    const doc = new jsPDF();
    
    // PDFのタイトル設定
    doc.setFontSize(20);
    doc.text('成績表 / Grade Report', 20, 30);
    
    // 評価対象情報
    doc.setFontSize(14);
    doc.text(`評価対象: ${decryptedTitle}`, 20, 50);
    
    if (product.reviewComment) {
      doc.setFontSize(10);
      const detailLines = doc.splitTextToSize(`詳細: ${product.reviewComment}`, 170);
      doc.text(detailLines, 20, 60);
    }
    
      let yPosition = 80;
    
    // 基本情報
    doc.setFontSize(12);
    doc.text(`作成日: ${new Date().toLocaleDateString('ja-JP')}`, 20, yPosition);
    yPosition += 10;
    
    // 評価詳細があれば追加
    if (product.reviewComment) {
      doc.setFontSize(10);
      const detailLines = doc.splitTextToSize(`詳細: ${product.reviewComment}`, 170);
      doc.text(detailLines, 20, yPosition);
      yPosition += detailLines.length * 5 + 10;
    }
    
    // PDF保存
    doc.save(`${decryptedTitle}_成績表.pdf`);
  };

  if (!isOpen || !product) return null;

  return (
    <div className="product-detail-modal-overlay" onClick={onClose}>
      <div className="product-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-detail-modal__header">
          <h3 className="product-detail-modal__title">評価対象の詳細</h3>
          <button 
            className="product-detail-modal__close" 
            onClick={onClose}
            aria-label="モーダルを閉じる"
          >
            <XIcon />
          </button>
        </div>

        <div className="product-detail-modal__content">
          {isEditing ? (
            // 編集モード
            <div className="product-detail-modal__edit">
              <div className="input-wrapper">
                <label className="input-label">評価対象名</label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="評価対象名を入力"
                />
              </div>

              <div className="input-wrapper">
                <label className="input-label">詳細・コメント</label>
                <textarea
                  className="input textarea"
                  value={editComment}
                  onChange={(e) => setEditDetail(e.target.value)}
                  placeholder="詳細やコメントを入力"
                  rows={4}
                />
              </div>

              <div className="input-wrapper">
                <label className="input-label">プレビューリンク（任意）</label>
                <Input
                  value={editLink}
                  onChange={(e) => setEditLink(e.target.value)}
                  placeholder="https://..."
                  type="url"
                />
              </div>

              <div className="product-detail-modal__edit-actions">
                <Button
                  variant="primary"
                  leftIcon={<CheckIcon />}
                  onClick={saveEdit}
                  disabled={!editTitle.trim() || !editComment.trim()}
                >
                  保存
                </Button>
                <Button
                  variant="ghost"
                  leftIcon={<XIcon />}
                  onClick={cancelEdit}
                >
                  キャンセル
                </Button>
              </div>
            </div>
          ) : (
            // 表示モード
            <div className="product-detail-modal__view">
              <div className="product-detail-modal__field">
                <label className="product-detail-modal__label">評価対象名</label>
                <div className="product-detail-modal__value product-detail-modal__value--title">
                  {decryptedTitle}
                </div>
              </div>

              <div className="product-detail-modal__field">
                <label className="product-detail-modal__label">詳細・コメント</label>
                <div className="product-detail-modal__value product-detail-modal__value--detail">
                  {product.reviewComment}
                </div>
              </div>

              {product.reviewLink && (
                <div className="product-detail-modal__field">
                  <label className="product-detail-modal__label">プレビューリンク</label>
                  <div className="product-detail-modal__value product-detail-modal__value--link">
                    {product.reviewLink}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="product-detail-modal__actions">
          {!isEditing && (
            <>
              <Button
                variant="secondary"
                leftIcon={<EditIcon />}
                onClick={startEdit}
              >
                編集
              </Button>
              {product.reviewLink && (
                <Button
                  variant="primary"
                  leftIcon={<PreviewIcon />}
                  onClick={handlePreview}
                >
                  プレビュー
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
